// ─────────────────────────────────────────────────────────────────
// PP MAP v5 — Scoring Engine
// Professional-grade index calculations based on Argus/ICIS methodology
// ─────────────────────────────────────────────────────────────────

// ── HELPERS ──────────────────────────────────────────────────────
const clamp = (v, min = 0, max = 100) => Math.max(min, Math.min(max, v));

// ── STI v2 — Supply Tightness Index ──────────────────────────────
// STI = A2(22%) + B2(28%) + C2(18%) + D2(20%) + E2(12%)
export function calcSTI(inputs) {
  const {
    plant_op_rate = 71,           // % — used for planned maintenance proxy
    hormuz_traffic_pct = 5,       // % of normal traffic
    vlcc_rate_premium = 100,      // % above normal rates (0 = normal)
    propylene_spot = 8500,        // ¥/MT
    pp_spot_qingdao = 10000,      // ¥/MT
    naphtha_crack = 80,           // $/MT
    china_export_cushion = false, // bool — China record exports acting as cushion
  } = inputs;
  const kaps_stock_mt        = inputs.kaps_stock_mt        ?? 1892; // ?? not || (0 valid)
  const kaps_daily_demand_mt = inputs.kaps_daily_demand_mt ?? 63.5; // ?? not || (0 valid)

  // C2 input: derive weeks of on-hand stock from KAPS data.
  // Excludes transit/PR — those are forward supply, not physical stock now.
  const inventory_weeks = kaps_daily_demand_mt > 0
    ? kaps_stock_mt / (kaps_daily_demand_mt * 7)
    : 2;

  // A2: Planned maintenance impact (22%)
  // Lower op rate = more maintenance = higher tightness
  // 85%+ = normal (score 20), 70% = tight (score 60), 60% = very tight (score 85)
  const a2 = clamp(plant_op_rate >= 85 ? 20 : plant_op_rate >= 75 ? 50 : plant_op_rate >= 65 ? 70 : 85);

  // B2: Hormuz import flow composite (28%)
  // Traffic % inversely drives score. VLCC rate premium multiplies it.
  const hormuzBase = clamp((1 - hormuz_traffic_pct / 100) * 100);
  const vlccMultiplier = 1 + (vlcc_rate_premium / 200); // max +50% amplification
  const b2 = clamp(hormuzBase * vlccMultiplier);

  // C2: Inventory-to-consumption ratio (18%)
  // 4+ weeks = loose (10), 3 wks = normal (35), 2 wks = tight (65), 1 wk = very tight (90)
  const c2 = clamp(
    inventory_weeks >= 4 ? 10 :
    inventory_weeks >= 3 ? 35 :
    inventory_weeks >= 2 ? 65 :
    inventory_weeks >= 1 ? 85 : 95
  );

  // D2: Propylene-PP spread (20%)
  // ¥900+ = normal (10), ¥600 = moderate (40), ¥300 = tight (75), <¥200 = crisis (95)
  const propPPSpread = pp_spot_qingdao - propylene_spot * 1.02;
  const d2 = clamp(
    propPPSpread >= 900 ? 10 :
    propPPSpread >= 600 ? 40 :
    propPPSpread >= 400 ? 65 :
    propPPSpread >= 200 ? 80 : 95
  );

  // E2: Naphtha crack spread (12%)
  // >$120 = supply squeeze (80), $80-120 = normal (45), <$60 = margin compression (15)
  const e2 = clamp(
    naphtha_crack > 150 ? 90 :
    naphtha_crack > 120 ? 80 :
    naphtha_crack > 80 ? 45 :
    naphtha_crack > 60 ? 25 : 15
  );

  // China export cushion adjustment (reduces STI slightly)
  const cushionAdj = china_export_cushion ? -5 : 0;

  const sti = clamp(a2 * 0.22 + b2 * 0.28 + c2 * 0.18 + d2 * 0.20 + e2 * 0.12 + cushionAdj);

  return {
    total: Math.round(sti),
    components: { a2: Math.round(a2), b2: Math.round(b2), c2: Math.round(c2), d2: Math.round(d2), e2: Math.round(e2) },
    label: sti >= 80 ? 'Extreme Tight' : sti >= 65 ? 'Tight' : sti >= 45 ? 'Moderate' : sti >= 25 ? 'Comfortable' : 'Loose',
    color: sti >= 75 ? 'red' : sti >= 55 ? 'amb' : 'grn',
  };
}

// ── MSI v2 — Market Stress Index (replaces PPI) ──────────────────
// MSI = F2(30%) + G2(28%) + H2(22%) + I2(20%)
export function calcMSI(inputs) {
  const {
    pp_spot_qingdao = 10000,    // ¥/MT China spot
    import_landed_cost = 10500, // ¥/MT estimated landed import cost
    dce_futures_m1 = 9200,      // DCE near month ¥/MT
    dce_futures_m3 = 9000,      // DCE 3-month ¥/MT
    bopp_op_rate = 65,           // % BOPP downstream operating rate
    supplier_allocation = 'open', // 'open','firm','restricted','suspended'
  } = inputs;

  // F2: Import arbitrage window closure (30%)
  // (China spot - import landed cost) / China spot * 100
  // Positive = import uneconomic = buyers captive = stress HIGH
  // Negative = imports competitive = stress LOW
  const arbWindow = (pp_spot_qingdao - import_landed_cost) / pp_spot_qingdao * 100;
  const f2 = clamp(
    arbWindow > 10 ? 90 :   // arb firmly closed — extreme stress
    arbWindow > 5 ? 70 :    // arb closed
    arbWindow > 0 ? 50 :    // marginally closed
    arbWindow > -5 ? 25 :   // marginally open
    10                       // arb wide open — imports cheap
  );

  // G2: Cash-futures basis (28%)
  // Spot - M1. Positive = backwardation = stress
  // ¥800+ basis = extreme stress (90), ¥400-800 = high (65), <¥200 = low (25)
  const cashFuturesBasis = pp_spot_qingdao - dce_futures_m1;
  const g2 = clamp(
    cashFuturesBasis > 800 ? 90 :
    cashFuturesBasis > 600 ? 75 :
    cashFuturesBasis > 400 ? 55 :
    cashFuturesBasis > 200 ? 35 :
    cashFuturesBasis > 0 ? 20 : 10
  );

  // H2: Forward curve shape — backwardation % (22%)
  // (M1 - M3) / M1 * 100
  // >5% backwardation = acute stress (90), flat = 35, contango = 10
  const backwardationPct = (dce_futures_m1 - dce_futures_m3) / dce_futures_m1 * 100;
  const h2 = clamp(
    backwardationPct > 5 ? 90 :
    backwardationPct > 3 ? 70 :
    backwardationPct > 1 ? 50 :
    backwardationPct > -1 ? 30 :
    backwardationPct > -3 ? 15 : 10
  );

  // I2: BOPP downstream operating rate (20%)
  // >85% = real demand (80), 75-85% = moderate (50), 65-75% = weak (30), <65% = very weak (10)
  const i2 = clamp(
    bopp_op_rate > 85 ? 80 :
    bopp_op_rate > 75 ? 50 :
    bopp_op_rate > 65 ? 30 : 10
  );

  // Supplier allocation adjustment
  const allocAdj = { open: 0, firm: 5, restricted: 15, suspended: 25 }[supplier_allocation] || 0;

  const msi = clamp(f2 * 0.30 + g2 * 0.28 + h2 * 0.22 + i2 * 0.20 + allocAdj);

  return {
    total: Math.round(msi),
    components: { f2: Math.round(f2), g2: Math.round(g2), h2: Math.round(h2), i2: Math.round(i2) },
    label: msi >= 75 ? 'High Stress' : msi >= 55 ? 'Elevated' : msi >= 35 ? 'Moderate' : 'Low Stress',
    color: msi >= 70 ? 'red' : msi >= 50 ? 'amb' : 'grn',
  };
}

// ── DPI — BOPP Demand Pressure Index ─────────────────────────────
export function calcDPI(inputs) {
  const {
    bopp_op_rate = 65,
    pp_spot_qingdao = 10000,
    conversion_cost = 1800,
  } = inputs;
  const kicp_portfolio_qty_mt      = inputs.kicp_portfolio_qty_mt      ?? 3290;  // ?? not || (0 valid)
  const kicp_portfolio_qty_prev_mt = inputs.kicp_portfolio_qty_prev_mt ?? 3989;  // ?? not || (0 valid)
  const kicp_gloss18_sell_price    = inputs.kicp_gloss18_sell_price    ?? 10800; // ?? not || (0 valid)

  // Utilisation score
  const utilScore = clamp(bopp_op_rate > 85 ? 80 : bopp_op_rate > 75 ? 60 : bopp_op_rate > 65 ? 40 : 25);

  // Order book momentum
  const orderMom = kicp_portfolio_qty_prev_mt > 0
    ? (kicp_portfolio_qty_mt - kicp_portfolio_qty_prev_mt) / kicp_portfolio_qty_prev_mt * 100
    : 0;
  const orderScore = clamp(orderMom > 10 ? 80 : orderMom > 5 ? 65 : orderMom > 0 ? 50 : orderMom > -5 ? 35 : 20);

  // Margin pressure (anchor product)
  const anchorMargin = kicp_gloss18_sell_price - pp_spot_qingdao * 1.03 - conversion_cost;
  const marginScore = clamp(anchorMargin > 1000 ? 20 : anchorMargin > 500 ? 35 : anchorMargin > 0 ? 50 : anchorMargin > -500 ? 65 : 80);

  const dpi = clamp(utilScore * 0.40 + orderScore * 0.35 + marginScore * 0.25);

  return {
    total: Math.round(dpi),
    label: dpi >= 70 ? 'High Pressure' : dpi >= 50 ? 'Moderate' : dpi >= 30 ? 'Low' : 'Weak',
    color: dpi >= 65 ? 'red' : dpi >= 45 ? 'amb' : 'grn',
    anchorMargin: Math.round(anchorMargin),
    components: { utilScore: Math.round(utilScore), orderScore: Math.round(orderScore), marginScore: Math.round(marginScore) },
  };
}

// ── OTI — Order Trend Index (-100 to +100) ────────────────────────
export function calcOTI(inputs) {
  const {
    net_revision_pct = 0,
  } = inputs;
  const kicp_portfolio_qty_mt         = inputs.kicp_portfolio_qty_mt         ?? 3290;  // ?? not || (0 valid)
  const kicp_portfolio_qty_prev_mt    = inputs.kicp_portfolio_qty_prev_mt    ?? 3989;  // ?? not || (0 valid)
  const kicp_portfolio_avg_sell       = inputs.kicp_portfolio_avg_sell       ?? 17249; // ?? not || (0 valid)
  const kicp_portfolio_avg_sell_prev  = inputs.kicp_portfolio_avg_sell_prev  ?? 14467; // ?? not || (0 valid)

  // Volume momentum (-100 to +100)
  const volMom = kicp_portfolio_qty_prev_mt > 0
    ? clamp((kicp_portfolio_qty_mt - kicp_portfolio_qty_prev_mt) / kicp_portfolio_qty_prev_mt * 100, -100, 100)
    : 0;

  // Price momentum (-100 to +100)
  const priceMom = kicp_portfolio_avg_sell_prev > 0
    ? clamp((kicp_portfolio_avg_sell - kicp_portfolio_avg_sell_prev) / kicp_portfolio_avg_sell_prev * 100, -100, 100)
    : 0;

  // Revision/cancellation component
  const revScore = clamp(-net_revision_pct * 10, -100, 100);

  const oti = volMom * 0.50 + priceMom * 0.30 + revScore * 0.20;

  return {
    total: Math.round(oti),
    label: oti > 20 ? 'Positive' : oti > -20 ? 'Stable' : 'Negative',
    color: oti > 20 ? 'grn' : oti > -20 ? 'amb' : 'red',
    components: { volMom: Math.round(volMom), priceMom: Math.round(priceMom), revScore: Math.round(revScore) },
  };
}

// ── BAYESIAN SCENARIO ENGINE ──────────────────────────────────────
// Historical base rates from similar conflicts (Gulf Wars, Yemen, Suez)
const BASE_RATES = {
  peace: 0.12,
  stalemate: 0.52,
  escalation: 0.26,
  strike: 0.10,
};

// Likelihood ratios: P(event|peace) / P(event|escalation)
const LIKELIHOOD_RATIOS = {
  vp_level_talks: { peace: 4.2, stalemate: 1.0, escalation: 0.24, strike: 0.10 },
  ship_attacked: { peace: 0.08, stalemate: 0.60, escalation: 3.5, strike: 4.0 },
  vessel_seizure: { peace: 0.04, stalemate: 0.40, escalation: 4.5, strike: 5.0 },
  ship_transited_safely: { peace: 3.1, stalemate: 1.5, escalation: 0.30, strike: 0.10 },
  deal_announced: { peace: 18.0, stalemate: 0.20, escalation: 0.05, strike: 0.02 },
  talks_collapsed: { peace: 0.05, stalemate: 0.50, escalation: 3.0, strike: 2.5 },
  strikes_announced: { peace: 0.01, stalemate: 0.10, escalation: 2.0, strike: 8.0 },
  eia_large_draw: { peace: 1.0, stalemate: 0.90, escalation: 1.1, strike: 1.1 },
  none: { peace: 1.0, stalemate: 1.0, escalation: 1.0, strike: 1.0 },
};

// Duration adjustment: conflicts extending >60 days resolve at higher rate
function durationAdjustment(conflictWeeks) {
  if (conflictWeeks < 4) return 1.0;
  if (conflictWeeks < 8) return 1.3;
  if (conflictWeeks < 12) return 1.7;
  return 2.0;
}

export function calcScenarios(inputs) {
  const {
    conflict_weeks = 10,
    recent_events = ['vp_level_talks'],
    polymarket_peace = null,
    pp_spot_qingdao = 10000,
    scenario_peace_low = 0.92,      scenario_peace_mid = 0.97,      scenario_peace_high = 1.02,
    scenario_stalemate_low = 1.00,  scenario_stalemate_mid = 1.05,  scenario_stalemate_high = 1.10,
    scenario_escalation_low = 1.10, scenario_escalation_mid = 1.20, scenario_escalation_high = 1.30,
    scenario_strike_low = 1.30,     scenario_strike_mid = 1.50,     scenario_strike_high = 1.70,
  } = inputs;

  // clamp to sane range; guards against missing fields in old saved runs
  const safeOffset = (v, fallback) => {
    const n = parseFloat(v);
    return (Number.isFinite(n) && n > 0.1 && n < 5.0) ? n : fallback;
  };

  // Start with base rates, apply duration adjustment
  const durAdj = durationAdjustment(conflict_weeks);
  let priors = {
    peace: Math.min(BASE_RATES.peace * durAdj, 0.60),
    stalemate: BASE_RATES.stalemate,
    escalation: BASE_RATES.escalation,
    strike: BASE_RATES.strike,
  };

  // Normalize priors
  const priorSum = Object.values(priors).reduce((a, b) => a + b, 0);
  Object.keys(priors).forEach(k => priors[k] /= priorSum);

  // Apply Bayesian update for each recent event
  // Defensive: handle non-array recent_events (DB null, manual row,
  // missing column). Destructuring defaults only fire for undefined.
  const eventsArr = Array.isArray(recent_events) ? recent_events : [];
  eventsArr.forEach(event => {
    const lr = LIKELIHOOD_RATIOS[event] || LIKELIHOOD_RATIOS.none;
    const unnorm = {
      peace: priors.peace * lr.peace,
      stalemate: priors.stalemate * lr.stalemate,
      escalation: priors.escalation * lr.escalation,
      strike: priors.strike * lr.strike,
    };
    const sum = Object.values(unnorm).reduce((a, b) => a + b, 0);
    Object.keys(priors).forEach(k => priors[k] = unnorm[k] / sum);
  });

  // Blend with Polymarket if provided (70:30)
  if (polymarket_peace !== null) {
    const blended_peace = 0.70 * priors.peace + 0.30 * polymarket_peace;
    const adjustment = blended_peace - priors.peace;
    priors.peace = blended_peace;
    priors.stalemate -= adjustment * 0.60;
    priors.escalation -= adjustment * 0.30;
    priors.strike -= adjustment * 0.10;
    // Re-normalize
    const sum = Object.values(priors).reduce((a, b) => a + b, 0);
    Object.keys(priors).forEach(k => priors[k] = Math.max(0.01, priors[k] / sum));
  }

  // Spot-anchored scenario bands (¥/MT): each value = spot × offset
  // peace: deal cools tensions, modest 3–8% downside
  // stalemate: status quo with mild upside drift
  // escalation: meaningful tension, 10–30% upside
  // strike: shock event, 30–70% upside
  const s = pp_spot_qingdao;
  const ppRanges = {
    peace:     { low: Math.round(s * safeOffset(scenario_peace_low, 0.92)),      mid: Math.round(s * safeOffset(scenario_peace_mid, 0.97)),      high: Math.round(s * safeOffset(scenario_peace_high, 1.02)) },
    stalemate: { low: Math.round(s * safeOffset(scenario_stalemate_low, 1.00)),  mid: Math.round(s * safeOffset(scenario_stalemate_mid, 1.05)),  high: Math.round(s * safeOffset(scenario_stalemate_high, 1.10)) },
    escalation:{ low: Math.round(s * safeOffset(scenario_escalation_low, 1.10)), mid: Math.round(s * safeOffset(scenario_escalation_mid, 1.20)), high: Math.round(s * safeOffset(scenario_escalation_high, 1.30)) },
    strike:    { low: Math.round(s * safeOffset(scenario_strike_low, 1.30)),     mid: Math.round(s * safeOffset(scenario_strike_mid, 1.50)),     high: Math.round(s * safeOffset(scenario_strike_high, 1.70)) },
  };

  // Expected value
  const ev7d = Object.keys(priors).reduce((sum, k) => {
    return sum + priors[k] * ppRanges[k].mid;
  }, 0);

  const p10 = Object.keys(priors).reduce((sum, k) => sum + priors[k] * ppRanges[k].low, 0);
  const p90 = Object.keys(priors).reduce((sum, k) => sum + priors[k] * ppRanges[k].high, 0);

  return {
    probabilities: {
      peace: Math.round(priors.peace * 100),
      stalemate: Math.round(priors.stalemate * 100),
      escalation: Math.round(priors.escalation * 100),
      strike: Math.round(priors.strike * 100),
    },
    pp_expected_7d: Math.round(ev7d),
    pp_p10_7d: Math.round(p10),
    pp_p90_7d: Math.round(p90),
    ppRanges,
  };
}

// ── FILM MARGIN CALCULATOR ────────────────────────────────────────
export function calcMargins(inputs) {
  const {
    pp_spot = 10000,
    conversion_cost = 1800,
    pp_yield_ratio = 1.03,
  } = inputs;
  const kicp_gloss18_sell_price = inputs.kicp_gloss18_sell_price ?? 10800; // ?? not || (0 valid)
  const kicp_portfolio_avg_sell = inputs.kicp_portfolio_avg_sell ?? 17249; // ?? not || (0 valid)

  const ppCost = pp_spot * pp_yield_ratio;
  const gloss18Margin = kicp_gloss18_sell_price - ppCost - conversion_cost;
  const portfolioMargin = kicp_portfolio_avg_sell - ppCost - conversion_cost;
  const gloss18Breakeven = (kicp_gloss18_sell_price - conversion_cost) / pp_yield_ratio;
  const gloss18BreakevenSellPrice = ppCost + conversion_cost;

  return {
    gloss18: Math.round(gloss18Margin),
    portfolio: Math.round(portfolioMargin),
    gloss18Breakeven: Math.round(gloss18Breakeven),
    gloss18BreakevenSellPrice: Math.round(gloss18BreakevenSellPrice),
    ppCost: Math.round(ppCost),
  };
}

// ── SAFETY STOCK CALCULATOR ───────────────────────────────────────
export function calcSafetyStatus(inputs) {
  const {
    lead_time_days = 35,
  } = inputs;
  const kaps_stock_mt        = inputs.kaps_stock_mt        ?? 1892; // ?? not || (0 valid)
  const kaps_transit_mt      = inputs.kaps_transit_mt      ?? 689;  // ?? not || (0 valid)
  const kaps_pr_mt           = inputs.kaps_pr_mt           ?? 387;  // ?? not || (0 valid)
  const kaps_daily_demand_mt = inputs.kaps_daily_demand_mt ?? 63.5; // ?? not || (0 valid)
  const kaps_safety_stock_mt = inputs.kaps_safety_stock_mt ?? 500;  // ?? not || (0 valid)

  const totalAvailable = kaps_stock_mt + kaps_transit_mt + kaps_pr_mt;
  const coverageDays = kaps_daily_demand_mt > 0 ? totalAvailable / kaps_daily_demand_mt : 999;
  const daysToSafetyBreach = kaps_stock_mt > 0
    ? (kaps_stock_mt - kaps_safety_stock_mt) / kaps_daily_demand_mt
    : 0;
  const orderArrivesIn = lead_time_days;
  const gapDays = Math.max(0, Math.round(orderArrivesIn - daysToSafetyBreach));

  return {
    totalAvailable: Math.round(totalAvailable),
    coverageDays: Math.round(coverageDays),
    daysToSafetyBreach: Math.round(daysToSafetyBreach),
    gapDays,
    isBreachImminent: daysToSafetyBreach < lead_time_days,
    urgency: daysToSafetyBreach < 7 ? 'critical' : daysToSafetyBreach < 14 ? 'urgent' : 'normal',
  };
}

// ── PROCUREMENT RECOMMENDATION ────────────────────────────────────
export function generateRecommendation(sti, msi, scenarios, safety, margins, inputs) {
  const { pp_spot_qingdao = 10000 } = inputs;
  const ev = scenarios.pp_expected_7d;
  const p10 = scenarios.pp_p10_7d;

  // Primary safety driver
  if (safety.urgency === 'critical') {
    return {
      action: 'BUY_NOW',
      label: 'Buy Now — Safety Critical',
      color: 'red',
      rationaleKey: 'rationale.safety_critical',
      rationaleParams: {
        days: safety.daysToSafetyBreach,
        target: Math.round(safety.gapDays * inputs.kaps_daily_demand_mt || 700),
      },
    };
  }

  // Price signal: if P10 (pessimistic scenario) > current spot × 1.05 → buy
  if (p10 > pp_spot_qingdao * 1.05) {
    return {
      action: 'BUY_NOW',
      label: 'Buy Now — Price Risk High',
      color: 'red',
      rationaleKey: 'rationale.p10_high',
      rationaleParams: {
        p10: `¥${p10.toLocaleString()}`,
        sti: sti.total,
      },
    };
  }

  // Expected value > spot → buy
  if (ev > pp_spot_qingdao * 1.02 && sti.total >= 65) {
    return {
      action: 'BUY_TRANCHE',
      label: 'Buy Tranche — Favourable Entry',
      color: 'amb',
      rationaleKey: 'rationale.ev_favourable',
      rationaleParams: {
        ev: `¥${ev.toLocaleString()}`,
        delta: `¥${(ev - pp_spot_qingdao).toLocaleString()}`,
        sti: sti.total,
      },
    };
  }

  // Candidate: peace scenario dominant → HOLD, watch for correction
  let candidateResult;
  if (scenarios.probabilities.peace >= 40) {
    candidateResult = {
      action: 'HOLD',
      label: 'Hold — Peace Signal Active',
      color: 'grn',
      rationaleKey: 'rationale.peace_active',
      rationaleParams: {
        peacePct: scenarios.probabilities.peace,
        ev: `¥${ev.toLocaleString()}`,
      },
    };
  } else {
    // Default
    candidateResult = {
      action: 'WAIT',
      label: 'Monitor — No Urgent Signal',
      color: 'grn',
      rationaleKey: 'rationale.monitor_default',
      rationaleParams: {
        sti: sti.total,
        msi: msi.total,
      },
    };
  }

  // 4-cell WAIT escalation matrix — only escalates HOLD → WAIT
  if (candidateResult.action === 'HOLD') {
    const coverageDays = safety.coverageDays || 0;
    const forecastPct = pp_spot_qingdao > 0
      ? (ev - pp_spot_qingdao) / pp_spot_qingdao * 100
      : 0;

    const inventoryCategory =
      coverageDays > 90 ? 'excessive' :
      coverageDays > 60 ? 'long'      :
      coverageDays > 45 ? 'comfortable' :
      coverageDays > 30 ? 'tight'     :
                          'critical';

    const forecastCategory =
      forecastPct <= -15 ? 'strong_down'   :
      forecastPct <= -5  ? 'moderate_down' :
      forecastPct <   5  ? 'flat'          :
      forecastPct <  15  ? 'moderate_up'   :
                           'strong_up';

    const shouldEscalateToWait = (
      (inventoryCategory === 'excessive' && forecastCategory === 'strong_down')   ||
      (inventoryCategory === 'excessive' && forecastCategory === 'moderate_down') ||
      (inventoryCategory === 'excessive' && forecastCategory === 'flat')          ||
      (inventoryCategory === 'long'      && forecastCategory === 'strong_down')
    );

    if (shouldEscalateToWait) {
      let rationaleKey;
      if (inventoryCategory === 'excessive' && forecastCategory === 'strong_down') {
        rationaleKey = 'rationale.wait_excess_strong_down';
      } else if (inventoryCategory === 'excessive') {
        rationaleKey = 'rationale.wait_excess_consume';
      } else {
        rationaleKey = 'rationale.wait_long_strong_down';
      }
      return {
        action: 'WAIT',
        label: 'Wait — Defer Entry',
        color: 'blu',
        rationaleKey,
        rationaleParams: { coverageDays, forecastPct: forecastPct.toFixed(1) },
      };
    }
  }

  return candidateResult;
}

// ── FULL SCORE CALCULATOR ─────────────────────────────────────────
export function runFullModel(inputs, aiAdjustments = {}) {
  const sti = calcSTI(inputs);
  const msi = calcMSI(inputs);
  const dpi = calcDPI(inputs);
  const oti = calcOTI(inputs);
  const scenarios = calcScenarios(inputs);
  const margins = calcMargins({ ...inputs, pp_spot: inputs.pp_spot_qingdao });
  const safety = calcSafetyStatus(inputs);
  // ── AI Signal Adjustments (Path C: transparent, bounded) ──
  const { stiDelta = 0, msiDelta = 0, dpiDelta = 0 } = aiAdjustments;
  if (stiDelta !== 0) {
    sti.total = Math.min(100, Math.max(0, sti.total + stiDelta));
    sti.aiAdjusted = true;
    sti.aiDelta = stiDelta;
    sti.label = sti.total >= 80 ? 'Extreme Tight' : sti.total >= 65 ? 'Tight' : sti.total >= 45 ? 'Moderate' : sti.total >= 25 ? 'Comfortable' : 'Loose';
    sti.color = sti.total >= 75 ? 'red' : sti.total >= 55 ? 'amb' : 'grn';
  }
  if (msiDelta !== 0) {
    msi.total = Math.min(100, Math.max(0, msi.total + msiDelta));
    msi.aiAdjusted = true;
    msi.aiDelta = msiDelta;
    msi.label = msi.total >= 75 ? 'High Stress' : msi.total >= 55 ? 'Elevated' : msi.total >= 35 ? 'Moderate' : 'Low Stress';
    msi.color = msi.total >= 70 ? 'red' : msi.total >= 50 ? 'amb' : 'grn';
  }
  if (dpiDelta !== 0) {
    dpi.total = Math.min(100, Math.max(0, dpi.total + dpiDelta));
    dpi.aiAdjusted = true;
    dpi.aiDelta = dpiDelta;
    dpi.label = dpi.total >= 70 ? 'High Pressure' : dpi.total >= 50 ? 'Moderate' : dpi.total >= 30 ? 'Low' : 'Weak';
    dpi.color = dpi.total >= 65 ? 'red' : dpi.total >= 45 ? 'amb' : 'grn';
  }

  const recommendation = generateRecommendation(sti, msi, scenarios, safety, margins, inputs);

  return { sti, msi, dpi, oti, scenarios, margins, safety, recommendation, computedAt: new Date().toISOString() };
}

// ── SEA PRICE CONTEXT ─────────────────────────────────────────────
export const SEA_CONTEXT = {
  structural_premium_usd: { low: 65, high: 100 },
  precrisis_bopp_cfr: { low: 950, high: 1010 },
  crisis_bopp_cfr: { low: 1210, high: 1370 },
  argus_mid: 1110,
  freight_china_to_sea: {
    vietnam_thailand: { low: 15, high: 30 },
    indonesia: { low: 40, high: 50 },
  },
};

export const PUBLIC_DATA_SOURCES = [
  { name: 'SCI99 (生意社)', url: 'https://www.sci99.com/price/chart/1001/', description: 'China PP daily spot prices — Zhenhai, Jiujiang, Daqing, propylene', category: 'China PP' },
  { name: 'SunSirs (生意社大宗)', url: 'https://www.sunsirs.com/uk/proDetail-40.html', description: 'PP benchmark daily index and trend commentary', category: 'China PP' },
  { name: 'DCE PP Futures (大商所)', url: 'https://www.dce.com.cn/dalianshangpin/sspz/pp/index.html', description: 'Dalian Commodity Exchange PP futures — M1, M3 contracts', category: 'Futures' },
  { name: 'Sina Finance DCE API (新浪财经)', url: 'https://finance.sina.com.cn/futures/', description: 'Source for DCE PP M1 close price — auto-fetched every 15 min via /api/live-prices CF proxy', category: 'Futures' },
  { name: 'Open Exchange Rates (open.er-api.com)', url: 'https://open.er-api.com/', description: 'USD/CNY exchange rate — auto-fetched directly every 15 min', category: 'China PP' },
  { name: 'Mysteel PP (我的钢铁)', url: 'https://pp.mysteel.com/', description: 'PP spot, operating rates, inventory data', category: 'China PP' },
  { name: 'CPCIF Weekly (中国石油化工)', url: 'http://www.cpcif.org.cn/', description: 'Polymer inventory survey, BOPP operating rates — published Mondays', category: 'China PP' },
  { name: 'Polymerupdate SEA PP', url: 'https://polymerupdate.com/prices/sea/polymers/pp', description: 'Weekly SEA CFR PP prices — Raffia, Film, BOPP, Injection by country', category: 'SEA' },
  { name: 'ICIS Polypropylene', url: 'https://www.icis.com/explore/commodities/chemicals/polypropylene/', description: 'PP market news, plant updates, SEA supply disruptions', category: 'SEA' },
  { name: 'ChemOrbis SEA', url: 'https://www.chemorbis.com/en/sea/southeast-asia-plastics-news-prices-analysis-pp-hdpe-ldpe-lldpe-gpps-hips-pvc-abs-pet', description: 'SEA polymer prices and market analysis', category: 'SEA' },
  { name: 'EIA Weekly Petroleum', url: 'https://www.eia.gov/petroleum/supply/weekly/', description: 'US crude inventory draw/build — weekly Wednesday release', category: 'Oil' },
  { name: 'EIA STEO', url: 'https://www.eia.gov/outlooks/steo/', description: 'Short-term energy outlook — supply projections', category: 'Oil' },
  { name: 'Investing.com WTI', url: 'https://www.investing.com/commodities/crude-oil', description: 'Live WTI and Brent crude oil prices', category: 'Oil' },
  { name: 'Baltic Exchange VLCC', url: 'https://www.balticexchange.com/en/index.html', description: 'VLCC freight rates — TD3C route (ME Gulf → China)', category: 'Freight' },
  { name: 'Argus Global PP (sample)', url: 'https://www.argusmedia.com/-/media/project/argusmedia/mainsite/english/documents-and-files/sample-reports/argus-global-polypropylene.pdf', description: 'Argus weekly PP report sample — SEA CFR prices, trade flows', category: 'SEA' },
  { name: 'Polymarket (Iran/Hormuz)', url: 'https://polymarket.com', description: 'Prediction market — Iran peace/Hormuz probability for Bayesian cross-check', category: 'Geopolitical' },
];
